import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatarBRL, formatarDataBR } from '@truefinance/shared';
import { Check, Mic, MessageSquare, X } from 'lucide-react';
import { useState } from 'react';
import { api, type ItemRevisao } from '../api/cliente.js';
import { Carregando, ErroSeguro, Vazio } from '../componentes/estados.js';
import { ModalAcao } from '../componentes/ModalAcao.js';
import { Badge } from '../componentes/Badge.js';

/**
 * Fila de revisão.
 *
 * Mostra a **mensagem original** ao lado da interpretação. Sem isso o operador
 * julgaria no escuro: ver "saída de R$ 150 em Alimentação" não diz se o sistema
 * entendeu certo — ver "gastei 150 no almoço" ao lado, sim.
 */
export function Revisao() {
  const cliente = useQueryClient();
  const [paraDescartar, setParaDescartar] = useState<ItemRevisao | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['revisao'],
    queryFn: () => api.revisao(),
  });

  async function confirmar(id: string) {
    setConfirmando(id);
    try {
      await api.confirmarRevisao(id);
      await cliente.invalidateQueries({ queryKey: ['revisao'] });
      await cliente.invalidateQueries({ queryKey: ['dashboard'] });
    } finally {
      setConfirmando(null);
    }
  }

  if (isLoading) return <Carregando />;
  if (isError || !data) return <ErroSeguro aoTentarNovamente={() => void refetch()} />;

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-[length:var(--text-tela)] font-semibold text-texto">Revisão</h1>
        <p className="mt-0.5 text-[length:var(--text-secundario)] text-texto-secundario">
          Lançamentos que o sistema entendeu, mas não com certeza suficiente para confirmar sozinho.
          Eles não entram no saldo até você decidir.
        </p>
      </header>

      {data.pendentes.length === 0 ? (
        <Vazio
          titulo="Nenhuma pendência"
          orientacao="Tudo que chegou foi entendido com clareza. Nada a revisar."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {data.pendentes.map((item) => (
            <Cartao
              key={item.transacao.id}
              item={item}
              confirmando={confirmando === item.transacao.id}
              aoConfirmar={() => void confirmar(item.transacao.id)}
              aoDescartar={() => setParaDescartar(item)}
            />
          ))}
        </div>
      )}

      <ModalAcao
        aberto={paraDescartar !== null}
        titulo="Descartar lançamento"
        variante="destrutiva"
        rotuloConfirmar="Descartar"
        impacto={
          paraDescartar && (
            <>
              <p>
                <strong>{paraDescartar.transacao.descricao}</strong>
              </p>
              <p className="mono mt-1">
                {paraDescartar.transacao.tipo === 'entrada' ? 'Entrada' : 'Saída'} de{' '}
                {formatarBRL(paraDescartar.transacao.valorCentavos)}
              </p>
              <p className="mt-2">
                O lançamento sai da fila e não entra no saldo. A mensagem original e o motivo ficam
                na auditoria.
              </p>
            </>
          )
        }
        aoConfirmar={async (motivo) => {
          if (!paraDescartar) return;
          await api.descartarRevisao(paraDescartar.transacao.id, motivo);
          await cliente.invalidateQueries({ queryKey: ['revisao'] });
        }}
        aoFechar={() => setParaDescartar(null)}
      />
    </div>
  );
}

function Cartao({
  item,
  confirmando,
  aoConfirmar,
  aoDescartar,
}: {
  item: ItemRevisao;
  confirmando: boolean;
  aoConfirmar: () => void;
  aoDescartar: () => void;
}) {
  const { transacao } = item;
  const original = item.mensagemTranscricao ?? item.mensagemTexto;
  const ehAudio = item.mensagemTipo === 'audio';

  return (
    <article className="rounded-[var(--radius-card)] border border-pendente-borda-sutil bg-superficie shadow-[var(--shadow-card)]">
      <div className="flex items-center gap-2 rounded-t-[var(--radius-card)] bg-pendente px-4 py-2 text-white">
        <Badge variante="pendente" />
        {transacao.confianca !== null && (
          <span className="mono ml-auto text-[length:var(--text-metadado)] opacity-90">
            confiança {Math.round(transacao.confianca * 100)}%
          </span>
        )}
      </div>

      <div className="p-4">
        {/* A mensagem original é o que permite julgar a sugestão. */}
        <div className="mb-3 rounded-[var(--radius-controle)] bg-fundo p-3">
          <p className="mb-1 flex items-center gap-1.5 text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario">
            {ehAudio ? <Mic size={11} aria-hidden /> : <MessageSquare size={11} aria-hidden />}
            {ehAudio ? 'Áudio transcrito' : 'Mensagem original'}
          </p>
          <p className="text-[length:var(--text-corpo)] italic text-texto-secundario">
            {original ? `"${original}"` : 'Mensagem não disponível'}
          </p>
        </div>

        <div className="mb-4">
          <p className="text-[length:var(--text-coluna)] font-semibold uppercase tracking-[0.05em] text-texto-terciario">
            Interpretação
          </p>
          <p className="mt-1 text-[length:var(--text-corpo)] text-texto">
            <span
              className={`mono font-semibold ${
                transacao.tipo === 'entrada' ? 'text-entrada' : 'text-saida'
              }`}
            >
              {transacao.tipo === 'entrada' ? 'Entrada' : 'Saída'} de{' '}
              {formatarBRL(transacao.valorCentavos)}
            </span>
            {item.categoria && <span className="text-texto-secundario"> · {item.categoria}</span>}
            <span className="mono text-texto-terciario">
              {' '}
              · {formatarDataBR(transacao.dataOcorrencia)}
            </span>
          </p>
          <p className="mt-0.5 text-[length:var(--text-secundario)] text-texto-secundario">
            {transacao.descricao}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={aoConfirmar}
            disabled={confirmando}
            className="inline-flex min-h-[38px] items-center gap-1.5 rounded-[var(--radius-controle)] bg-entrada px-4 text-[length:var(--text-secundario)] font-medium text-white hover:bg-[#136A40] disabled:opacity-45"
          >
            <Check size={14} aria-hidden />
            {confirmando ? 'Confirmando...' : 'Confirmar'}
          </button>
          <button
            type="button"
            onClick={aoDescartar}
            className="inline-flex min-h-[38px] items-center gap-1.5 rounded-[var(--radius-controle)] border border-saida-borda px-4 text-[length:var(--text-secundario)] font-medium text-saida hover:bg-saida-bg"
          >
            <X size={14} aria-hidden />
            Descartar...
          </button>
        </div>
      </div>
    </article>
  );
}
